import type { ComponentProps } from "react";
import { cn } from "../../lib/utils";
import { useAutoResizeTextarea } from "../../hooks/use-auto-resize-textarea";

export type TextareaProps = ComponentProps<"textarea"> & {
  /**
   * A caixa cresce com o conteúdo em vez de rolar (TUT-249 fase 2).
   *
   * **Opcional, e não o padrão — por ora.** A direção do kokonutui chama este
   * item de "primeiro passo recomendado" e de **teste do caminho**: se ele
   * entra limpo, os outros têm molde. Ligar para os 11 usos de uma vez trocaria
   * a altura de caixas que a baseline de estilo mede, e misturaria o teste com
   * uma regressão visual em massa — exatamente o que a direção manda evitar.
   * Quando o caminho estiver provado, o padrão inverte num gesto só.
   */
  autoResize?: boolean;
  /** Teto em px; passando dele, a rolagem volta. Sem teto, cresce à vontade. */
  alturaMaxima?: number;
};

// Textarea do design system, calibrado 1:1 com `.field input` de styles.css
// (borda forte, fundo surface-2, cantos 6px e anel de foco primário), com
// padding vertical e redimensionamento vertical por ser multilinha. Dentro de
// `.field` padroniza o textarea com os inputs (que já usam esse look).
export function Textarea({
  className,
  autoResize = false,
  alturaMaxima,
  onChange,
  ...props
}: TextareaProps) {
  const { ref, ajustar } = useAutoResizeTextarea(
    autoResize,
    props.value ?? props.defaultValue,
    alturaMaxima,
  );

  return (
    <textarea
      ref={ref}
      className={cn(
        // TUT-282 — `leading-[1.35]` explícito: sem ele o texto herda o
        // `line-height: 1.5` do `body` via `font: inherit` (base.css), que com
        // a fonte menor (0.86rem) deixa a primeira linha com respiro
        // inconsistente em relação ao `py-[0.6rem]` desta caixa.
        "w-full px-[0.75rem] py-[0.6rem] rounded-[6px] border border-border-strong bg-surface-2 text-foreground leading-[1.35] outline-none focus:border-focus focus:shadow-[0_0_0_3px_var(--focus-highlight)]",
        // `resize-y` sai quando a caixa se ajusta sozinha: deixar a alça ali
        // seria oferecer um gesto que o próximo caractere desfaz.
        autoResize ? "resize-none" : "resize-y",
        className,
      )}
      onChange={(evento) => {
        // Ajusta ANTES de avisar quem controla o campo: num campo controlado o
        // valor só volta no render seguinte, e medir depois faria a caixa
        // ficar sempre uma tecla atrás.
        ajustar();
        onChange?.(evento);
      }}
      {...props}
    />
  );
}
